export type ZoomLevel = 'month' | 'quarter' | 'year';

export function pixelsPerDay(zoom: ZoomLevel): number {
  switch (zoom) {
    case 'month': return 20;
    case 'quarter': return 5;
    case 'year': return 1.5;
  }
}

export function defaultVisibleRange(now: Date = new Date()): { start: Date; end: Date } {
  const start = new Date(now);
  start.setDate(start.getDate() - 14);
  const end = new Date(now);
  end.setDate(end.getDate() + 90);
  return { start, end };
}

export function daysBetween(a: Date, b: Date): number {
  return (b.getTime() - a.getTime()) / (1000 * 60 * 60 * 24);
}

export function barLeftPx(barStart: Date | null, rangeStart: Date, zoom: ZoomLevel): number {
  const start = barStart ?? rangeStart;
  return daysBetween(rangeStart, start) * pixelsPerDay(zoom);
}

export function barWidthPx(
  barStart: Date | null, barEnd: Date | null,
  rangeStart: Date, rangeEnd: Date, zoom: ZoomLevel,
): number {
  const s = barStart ?? rangeStart;
  const e = barEnd ?? rangeEnd;
  return Math.max(daysBetween(s, e) * pixelsPerDay(zoom), 1);
}

export function nowLineLeftPx(now: Date, rangeStart: Date, zoom: ZoomLevel): number {
  return daysBetween(rangeStart, now) * pixelsPerDay(zoom);
}

export interface BarPalette {
  fill: string;
  ring: string;
  text: string;
  badgeFill: string;
  badgeText: string;
}

export function priorityPalette(priority: number, isAlwaysOn: boolean): BarPalette {
  if (isAlwaysOn) {
    return {
      fill: 'bg-ff-mint-deep/70',
      ring: 'ring-1 ring-ff-forest/15',
      text: 'text-ff-forest',
      badgeFill: 'bg-white/70',
      badgeText: 'text-ff-forest',
    };
  }
  if (priority >= 50) {
    return {
      fill: 'bg-ff-forest',
      ring: 'ring-1 ring-ff-forest/40',
      text: 'text-ff-ivory',
      badgeFill: 'bg-ff-ivory/15',
      badgeText: 'text-ff-ivory',
    };
  }
  if (priority >= 10) {
    return {
      fill: 'bg-ff-forest-soft',
      ring: 'ring-1 ring-ff-forest/30',
      text: 'text-ff-ivory',
      badgeFill: 'bg-ff-ivory/15',
      badgeText: 'text-ff-ivory',
    };
  }
  if (priority > 0) {
    return {
      fill: 'bg-ff-sand',
      ring: 'ring-1 ring-ff-forest/15',
      text: 'text-ff-forest',
      badgeFill: 'bg-white/70',
      badgeText: 'text-ff-forest',
    };
  }
  return {
    fill: 'bg-ff-paper',
    ring: 'ring-1 ring-ff-mute/20',
    text: 'text-ff-forest',
    badgeFill: 'bg-white/80',
    badgeText: 'text-ff-mute',
  };
}

/** @deprecated kept for callsites still using the old API */
export function priorityColor(priority: number): string {
  if (priority === 0) return 'bg-ff-paper';
  if (priority < 10) return 'bg-ff-sand';
  if (priority < 50) return 'bg-ff-forest-soft';
  return 'bg-ff-forest';
}

export interface AxisTick {
  date: Date;
  leftPx: number;
  label: string;
  major: boolean;
}

const MONTH_NAMES = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
];

function startOfDay(d: Date): Date {
  const out = new Date(d);
  out.setHours(0, 0, 0, 0);
  return out;
}

export function axisTicks(rangeStart: Date, rangeEnd: Date, zoom: ZoomLevel): AxisTick[] {
  const ticks: AxisTick[] = [];
  const ppd = pixelsPerDay(zoom);
  // First-of-month majors are always emitted. Minors: week (Mon) at month-zoom,
  // month numbers at quarter-zoom, quarter starts at year-zoom.
  const cursor = startOfDay(rangeStart);
  while (cursor <= rangeEnd) {
    const isFirstOfMonth = cursor.getDate() === 1;
    const left = daysBetween(rangeStart, cursor) * ppd;
    if (isFirstOfMonth) {
      const showYear = cursor.getMonth() === 0 || ticks.length === 0;
      ticks.push({
        date: new Date(cursor),
        leftPx: left,
        label: showYear
          ? `${MONTH_NAMES[cursor.getMonth()]} ${cursor.getFullYear()}`
          : MONTH_NAMES[cursor.getMonth()],
        major: true,
      });
    } else if (zoom === 'month' && cursor.getDay() === 1) {
      ticks.push({
        date: new Date(cursor),
        leftPx: left,
        label: String(cursor.getDate()),
        major: false,
      });
    } else if (zoom === 'quarter' && cursor.getDate() === 15) {
      // mid-month minor tick at quarter zoom
      ticks.push({
        date: new Date(cursor),
        leftPx: left,
        label: '·',
        major: false,
      });
    }
    cursor.setDate(cursor.getDate() + 1);
  }
  return ticks;
}

export function formatBarRange(
  from: string | null | undefined,
  to: string | null | undefined,
): string {
  const f = from ? new Date(from) : null;
  const t = to ? new Date(to) : null;
  const fmt = (d: Date) =>
    `${d.getDate()} ${MONTH_NAMES[d.getMonth()]}`;
  if (!f && !t) return 'always on';
  if (f && !t) return `from ${fmt(f)}`;
  if (!f && t) return `until ${fmt(t)}`;
  return `${fmt(f!)} → ${fmt(t!)}`;
}

export function barsOverlapInRange(
  a: { active_from: string | null; active_to: string | null },
  b: { active_from: string | null; active_to: string | null },
  rangeStart: Date, rangeEnd: Date,
): boolean {
  const aStart = a.active_from ? new Date(a.active_from) : rangeStart;
  const aEnd = a.active_to ? new Date(a.active_to) : rangeEnd;
  const bStart = b.active_from ? new Date(b.active_from) : rangeStart;
  const bEnd = b.active_to ? new Date(b.active_to) : rangeEnd;
  return aStart < bEnd && bStart < aEnd;
}
